import { Trans } from "@lingui/react/macro";
import { useSession } from "@pontimed/auth-client";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Building2,
  Briefcase,
  Database,
  FileCheck2,
  Globe2,
  Receipt,
  ScrollText,
  Settings,
  Stethoscope,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import { useAdminCountries } from "@/hooks/use-admin-countries";
import { LEVEL_CONFIG, type CountryLevel } from "@/lib/countries";

/**
 * Admin → Dashboard.
 *
 * Day 1 stub had section grid only. Day 5 replaces with real KPI surface
 * matching legacy `src/app/admin/dashboard-client.tsx`:
 *   - 6 stat cards (providers / patients / appointments / revenue /
 *     totalCountries / openCountries) — providers/patients/appointments/
 *     revenue stay placeholder "0" until V1.1 wires real aggregates from
 *     apps/api (legacy itself ships them as "0" — no aggregation endpoint
 *     exists yet)
 *   - Active countries card (via useAdminCountries hook + LEVEL_CONFIG pill)
 *   - Quick actions tiles linking to admin sub-pages
 *
 * Per D21: 1:1 with legacy intent. Track 5 had section grid placeholder
 * Day 1-4 (V1.0 sprint pragmatic). Day 5 grows it into the real layout.
 */

function getCountryLevel(country: {
  platformStatus?: string;
  totalFacilities?: number;
  totalProviders?: number;
  totalClaimed?: number;
  providersWithSchedule?: number;
}): CountryLevel {
  if (!country.platformStatus || country.platformStatus === "hidden") return "hidden";
  if (country.platformStatus === "coming_soon") return "coming_soon";
  const claimed = country.totalClaimed ?? 0;
  const withSchedule = country.providersWithSchedule ?? 0;
  if (withSchedule >= 50) return "booking";
  if (claimed >= 1) return "directory";
  return "seeding";
}

interface QuickAction {
  to:
    | "/users"
    | "/providers"
    | "/facilities"
    | "/businesses"
    | "/claims"
    | "/subscriptions"
    | "/scraping"
    | "/settings"
    | "/audit";
  icon: LucideIcon;
  label: React.ReactNode;
}

const QUICK_ACTIONS: QuickAction[] = [
  { to: "/providers", icon: Stethoscope, label: <Trans>Providers review</Trans> },
  { to: "/claims", icon: FileCheck2, label: <Trans>Claim queue</Trans> },
  { to: "/scraping", icon: Database, label: <Trans>Run scraping</Trans> },
  { to: "/settings", icon: Settings, label: <Trans>Settings</Trans> },
];

interface SectionTile {
  to: QuickAction["to"];
  icon: LucideIcon;
  title: React.ReactNode;
  description: React.ReactNode;
}

const SECTIONS: SectionTile[] = [
  {
    to: "/users",
    icon: Users,
    title: <Trans>Users</Trans>,
    description: <Trans>Patient + staff accounts, role assignments.</Trans>,
  },
  {
    to: "/providers",
    icon: Stethoscope,
    title: <Trans>Providers</Trans>,
    description: <Trans>Doctor profiles, verification, claim flows.</Trans>,
  },
  {
    to: "/facilities",
    icon: Building2,
    title: <Trans>Facilities</Trans>,
    description: <Trans>Clinics, hospitals, pharmacies, labs.</Trans>,
  },
  {
    to: "/businesses",
    icon: Briefcase,
    title: <Trans>Businesses</Trans>,
    description: <Trans>Pharmacy chains, lab networks, equipment suppliers.</Trans>,
  },
  {
    to: "/claims",
    icon: FileCheck2,
    title: <Trans>Claims</Trans>,
    description: <Trans>Provider/facility/business claim review queue.</Trans>,
  },
  {
    to: "/subscriptions",
    icon: Receipt,
    title: <Trans>Subscriptions</Trans>,
    description: <Trans>Plans, invoices, refunds, payouts.</Trans>,
  },
  {
    to: "/scraping",
    icon: Database,
    title: <Trans>Scraping</Trans>,
    description: <Trans>Sources, jobs, run controls.</Trans>,
  },
  {
    to: "/settings",
    icon: Settings,
    title: <Trans>Settings</Trans>,
    description: <Trans>Stripe integration, languages, AI providers.</Trans>,
  },
  {
    to: "/audit",
    icon: ScrollText,
    title: <Trans>Audit log</Trans>,
    description: <Trans>Security events + GDPR-relevant traces (V1.1 wire).</Trans>,
  },
];

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: DashboardPage,
});

function DashboardPage() {
  const { user } = useSession();
  const { countries } = useAdminCountries();

  const visibleCountries = countries.filter((c) => c.platformStatus !== "hidden");
  const openCount = visibleCountries.length;

  const stats = [
    {
      label: <Trans>Providers</Trans>,
      value: "0",
      icon: "👨‍⚕️",
      color: "text-emerald-600",
      hint: <Trans>aggregate V1.1</Trans>,
    },
    {
      label: <Trans>Patients</Trans>,
      value: "0",
      icon: "👤",
      color: "text-blue-600",
      hint: <Trans>aggregate V1.1</Trans>,
    },
    {
      label: <Trans>Appointments</Trans>,
      value: "0",
      icon: "📅",
      color: "text-purple-600",
      hint: <Trans>aggregate V1.1</Trans>,
    },
    {
      label: <Trans>Revenue</Trans>,
      value: "€0",
      icon: "💰",
      color: "text-amber-600",
      hint: <Trans>MRR V1.1</Trans>,
    },
    {
      label: <Trans>Countries</Trans>,
      value: String(countries.length),
      icon: "🌍",
      color: "text-indigo-600",
      hint: null,
    },
    {
      label: <Trans>Open countries</Trans>,
      value: String(openCount),
      icon: "✅",
      color: "text-teal-600",
      hint: null,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          <Trans>Admin dashboard</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>
            Signed in as <strong>{user?.userId}</strong> · role <strong>{user?.role}</strong>
          </Trans>
        </p>
      </header>

      <section className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {stats.map((stat, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-2xl">{stat.icon}</span>
              </div>
              <div className={`mt-2 text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-muted-foreground text-xs">{stat.label}</div>
              {stat.hint && (
                <div className="text-muted-foreground/70 mt-0.5 text-[0.625rem] italic">
                  {stat.hint}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe2 className="size-4" />
              <Trans>Active markets ({openCount})</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {visibleCountries.length === 0 ? (
              <div className="text-muted-foreground py-4 text-sm">
                <Trans>No countries with platform status set.</Trans>
              </div>
            ) : (
              <div className="space-y-3">
                {visibleCountries.map((c) => {
                  const level = getCountryLevel({
                    platformStatus: c.platformStatus,
                    totalFacilities: c.totalFacilities,
                    totalProviders: c.totalProviders,
                    totalClaimed: c.totalClaimed,
                  });
                  const cfg = LEVEL_CONFIG[level];
                  return (
                    <div
                      key={c.code}
                      className="border-border/50 flex items-center gap-3 rounded-lg border p-3"
                    >
                      <span className="text-2xl">{c.flag ?? "🏳️"}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">
                          {c.name?.en || c.name?.ro || c.code}
                        </div>
                        <div className="text-muted-foreground text-xs">
                          <Trans>
                            {c.totalProviders} providers · {c.totalFacilities} facilities
                          </Trans>
                        </div>
                      </div>
                      <Badge variant="outline" className={cfg.color}>
                        {cfg.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              <Trans>Quick actions</Trans>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2">
              {QUICK_ACTIONS.map((action, i) => {
                const Icon = action.icon;
                return (
                  <Link
                    key={i}
                    to={action.to}
                    className="border-border/50 hover:border-primary/30 flex items-center gap-3 rounded-xl border p-4 transition-all hover:shadow-sm"
                  >
                    <Icon className="text-muted-foreground size-5" />
                    <span className="text-sm font-medium">{action.label}</span>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <section>
        <h2 className="text-muted-foreground mb-3 text-xs font-semibold tracking-wide uppercase">
          <Trans>All sections</Trans>
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <Link key={section.to} to={section.to} className="group/tile block">
                <Card className="hover:ring-primary/40 h-full transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="bg-muted flex size-9 items-center justify-center rounded-lg">
                        <Icon className="text-muted-foreground size-4" />
                      </div>
                    </div>
                    <CardTitle>{section.title}</CardTitle>
                    <CardDescription>{section.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <span className="text-primary text-xs font-medium">
                      <Trans>Open →</Trans>
                    </span>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
